<script setup>
import { onMounted, ref, computed } from 'vue';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Inertia } from '@inertiajs/inertia';
import { Head, Link, useForm, usePage } from '@inertiajs/inertia-vue3';
import VueCal from "vue-cal";
import 'vue-cal/dist/vuecal.css';
import CardBoxModal from "@/Components/CardBoxModal.vue";
import PremiumPageHeader from "@/Components/PremiumPageHeader.vue";
import BaseButton from "@/Components/BaseButton.vue";
import BaseIcon from "@/Components/BaseIcon.vue";
import axios from 'axios';
import {
    mdiCalendar,
    mdiCalendarMonth,
    mdiCalendarToday,
    mdiPlusCircle,
    mdiPencil,
    mdiTrashCan,
    mdiInformation,
    mdiCheckCircle,
    mdiClockOutline,
    mdiLockOutline,
    mdiSchool,
    mdiBookOpenVariant,
    mdiChevronLeft,
    mdiChevronRight,
    mdiAccountGroup,
    mdiFilterOutline,
    mdiRefresh
} from "@mdi/js";

const props = defineProps({
    events: {
        type: Array,
        default: () => []
    },
    origin_type: {
        type: String,
        default: 'agenda'
    },
    id_user: Number,
    isMaster: Boolean,
    courses: {
        type: Array,
        default: () => []
    },
    canCreate: {
        type: Boolean,
        default: true
    },
    canUpdate: {
        type: Boolean,
        default: true
    },
    canDelete: {
        type: Boolean,
        default: true
    },
    canCreateInstitutional: {
        type: Boolean,
        default: false
    },
    idColaborador: {
        type: Number,
        default: 0
    }
});

// Pestaña Principal activa: 'calendar' | 'student_agenda'
const activeMainTab = ref('calendar');

// Modal de detalle y confirmación
const showDialog = ref(false);
const selectedEvent = ref({});
const isModalActive = ref(false);
const isModalDangerActive = ref(false);
const titleModal = ref('');
const rowID = ref(0);
const rowAction = ref('');
const errorEvent = ref('');

// Filtro de tipo de evento en el calendario
const filterEventType = ref('all'); // 'all' | 'institucional' | 'user_private'
const calendarView = ref('week'); // 'month' | 'week'
const viewDisplayMode = ref('calendar'); // 'calendar' | 'list'

// Agenda escolar del estudiante
const getLocalDateString = (d = new Date()) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getLocalDateTimeString = (d = new Date()) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${mins}`;
};

const page = usePage();

// Permiso para crear eventos institucionales (por defecto administradores y secretarias)
const canCreateInstitutionalEvent = computed(() => {
    if (props.canCreateInstitutional) {
        return true;
    }
    const user = page.props.value?.auth?.user;
    if (!user) return false;
    // Superadmin o roles autorizados por defecto (Administradores y Secretarias)
    if (user.id === 1 || user.id === 2 || [1, 2, 3, 4, 6, 14].includes(user.id_rol)) {
        return true;
    }
    const rights = page.props.value?.auth?.rights;
    if (rights?.is_superadmin) return true;
    if (rights?.map?.agenda?.institucional) return true;
    if (Array.isArray(rights?.slugs) && rights.slugs.includes('agenda.institucional')) return true;
    return false;
});

const canModifyEvent = (event) => {
    if (!event) return false;
    // Si tiene permiso institucional o es admin/secretaria puede modificar
    if (canCreateInstitutionalEvent.value) return true;
    // Si es colaborador estándar, solo puede modificar sus propios eventos privados
    return event.id_user === props.id_user && (event.type_source === 'user_private' || !event.type_source);
};

// Determinar si el usuario logueado es estudiante o apoderado
const isStudent = computed(() => {
    if (props.idColaborador > 0) return false;
    if (props.origin_type === 'student') return true;
    const rol = Number(page.props.value?.auth?.user?.id_rol);
    return rol === 8 || rol === 9;
});

const studentAgendaData = ref(null);
const studentAgendaLoading = ref(false);
const studentAgendaDate = ref(getLocalDateString());

const currentDayOfWeek = computed(() => {
    if (studentAgendaData.value?.header?.dayOfWeek) {
        return Number(studentAgendaData.value.header.dayOfWeek);
    }
    if (studentAgendaDate.value) {
        const [y, m, d] = studentAgendaDate.value.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        const jsDay = dt.getDay(); // 0 es Domingo, 1 es Lunes ... 6 es Sábado
        return jsDay === 0 ? 7 : jsDay;
    }
    return 1;
});

const currentDayName = computed(() => {
    return studentAgendaData.value?.header?.dayName || studentAgendaData.value?.date?.dayName || '';
});

const currentFormattedDate = computed(() => {
    const h = studentAgendaData.value?.header;
    if (h && h.dayNumber && h.monthName && h.year) {
        return `${h.dayNumber} de ${h.monthName} ${h.year}`;
    }
    return studentAgendaData.value?.date?.formatted || studentAgendaDate.value;
});

const agendaItemsList = computed(() => {
    return studentAgendaData.value?.agendaItems || studentAgendaData.value?.agenda_items || [];
});

const attendanceTextDisplay = computed(() => {
    return studentAgendaData.value?.studentReport?.attendanceText || studentAgendaData.value?.attendance?.text || 'ASISTIÓ';
});

const todaySchedulesList = computed(() => {
    const list = studentAgendaData.value?.schedules || [];
    return list.filter(s => Number(s.day_of_week) === currentDayOfWeek.value);
});

const switchStudentTab = () => {
    activeMainTab.value = 'student_agenda';
    if (!studentAgendaData.value) {
        loadStudentAgenda();
    }
};

const loadStudentAgenda = (dateStr = '') => {
    studentAgendaLoading.value = true;
    const targetDate = dateStr || studentAgendaDate.value || getLocalDateString();
    studentAgendaDate.value = targetDate;

    axios.get('/student/dashboard/agenda', { params: { date: targetDate }, timeout: 10000 })
        .then(response => {
            studentAgendaData.value = response.data || null;
        })
        .catch(err => {
            console.warn('Error al cargar agenda escolar:', err);
        })
        .finally(() => {
            studentAgendaLoading.value = false;
        });
};

const changeStudentAgendaDay = (offset) => {
    const [y, m, d] = (studentAgendaDate.value || getLocalDateString()).split('-').map(Number);
    const current = new Date(y, m - 1, d);
    current.setDate(current.getDate() + offset);
    const newDateStr = getLocalDateString(current);
    studentAgendaDate.value = newDateStr;
    loadStudentAgenda(newDateStr);
};

// Formulario de creación / edición de evento
const formEvent = useForm({
    id: 0,
    start: '',
    end: '',
    title: '',
    contentFull: '',
    type_source: 'user_private'
});

// Eventos filtrados y normalizados para VueCal
const formattedEvents = computed(() => {
    let list = props.events || [];

    if (filterEventType.value !== 'all') {
        list = list.filter(e => {
            const type = e.type_source || e.class || 'user_private';
            return type === filterEventType.value;
        });
    }

    return list.map(e => {
        const type = e.type_source || e.class || 'user_private';
        return {
            id: e.id,
            start: e.start_datetime || e.start,
            end: e.end_datetime || e.end,
            title: e.title || 'Sin título',
            content: e.content || '',
            contentFull: e.contentFull || e.content_full || e.content || '',
            class: type,
            type_source: type,
            id_user: e.id_user,
            deletable: false,
            resizable: false
        };
    });
});

// Estadísticas de eventos para contadores
const institutionalCount = computed(() => {
    return (props.events || []).filter(e => (e.type_source || e.class) === 'institucional').length;
});
const privateCount = computed(() => {
    return (props.events || []).filter(e => (e.type_source || e.class || 'user_private') === 'user_private').length;
});

// Al hacer clic en un evento del calendario
const onEventClick = (event, e) => {
    selectedEvent.value = event;
    showDialog.value = true;
    if (e && e.stopPropagation) {
        e.stopPropagation();
    }
};

const initModal = (id = 0, action = 'create', data = {}) => {
    rowAction.value = action;
    rowID.value = id;
    errorEvent.value = '';

    if (action === 'update') {
        showDialog.value = false;
        formEvent.id = data.id;
        formEvent.start = data.start_datetime || data.start;
        formEvent.end = data.end_datetime || data.end;
        formEvent.title = data.title;
        formEvent.contentFull = data.contentFull || data.content_full || data.content;
        formEvent.type_source = data.type_source || data.class || 'user_private';
        isModalActive.value = true;
    } else if (action === 'delete') {
        titleModal.value = 'eliminar';
        showDialog.value = false;
        isModalDangerActive.value = true;
        formEvent.id = rowID.value;
    } else {
        cleanVal();
        // Sugerir fecha y hora actual local para el nuevo evento
        const now = new Date();
        const startStr = getLocalDateTimeString(now);
        const later = new Date(now.getTime() + 60 * 60 * 1000);
        const endStr = getLocalDateTimeString(later);
        formEvent.start = startStr;
        formEvent.end = endStr;
        formEvent.type_source = canCreateInstitutionalEvent.value ? 'institucional' : 'user_private';
        isModalActive.value = true;
    }
};

const isConfirm = () => {
    eventUpdateOrCreate(rowID.value, rowAction.value);
};

const isCancel = () => {
    isModalActive.value = false;
    cleanVal();
};

const cleanVal = () => {
    rowID.value = 0;
    rowAction.value = '';
    errorEvent.value = '';
    formEvent.id = 0;
    formEvent.start = '';
    formEvent.end = '';
    formEvent.title = '';
    formEvent.contentFull = '';
    formEvent.type_source = canCreateInstitutionalEvent.value ? 'institucional' : 'user_private';
};

const eventUpdateOrCreate = (id = 0, action = '') => {
    let ruta = 'event-registrar';
    if (id > 0) {
        ruta = 'event-actualizar';
        if (action === 'delete') {
            ruta = 'event-eliminar';
            axios.post(route(ruta), { id: formEvent.id })
                .then((response) => {
                    if (response.data.error) {
                        errorEvent.value = response.data.error;
                    } else {
                        Inertia.reload({ only: ['events'] });
                        isModalDangerActive.value = false;
                        cleanVal();
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            return;
        }
    }

    if (!formEvent.title || !formEvent.start || !formEvent.end) {
        errorEvent.value = 'Debe ingresar un título y rango de fechas de inicio y fin';
        return;
    }

    errorEvent.value = '';
    axios.post(route(ruta), formEvent)
        .then((response) => {
            if (response.data.error) {
                errorEvent.value = response.data.error;
            } else {
                Inertia.reload({ only: ['events'] });
                isModalActive.value = false;
                cleanVal();
            }
        })
        .catch((error) => {
            console.error(error);
        });
};

// Formato de fechas amigable
const formatEventDate = (dateVal) => {
    if (!dateVal) return '-';
    const d = new Date(dateVal);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
};

onMounted(() => {
    if (isStudent.value) {
        activeMainTab.value = 'student_agenda';
        loadStudentAgenda();
    } else {
        activeMainTab.value = 'calendar';
    }
});
</script>

<template>
    <Head title="Agenda Institucional y Escolar" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader 
                title="Agenda Institucional & Escolar" 
                subtitle="Gestión de actividades oficiales, calendario del colegio y seguimiento diario de clases." 
                icon="solar:calendar-date-bold-duotone" 
                color="indigo"
            >
                <template #actions>
                    <button
                        v-if="canCreate"
                        type="button"
                        @click="initModal(0, 'create')"
                        class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-sm shadow-indigo-500/20 active:scale-95 transition-all cursor-pointer"
                    >
                        <BaseIcon :path="mdiPlusCircle" :size="18" />
                        <span>Nuevo Evento</span>
                    </button>
                </template>
            </PremiumPageHeader>
        </template>

        <!-- Modal de Detalle de Evento -->
        <CardBoxModal 
            v-model="showDialog" 
            hasCancel 
            :title="selectedEvent.title || 'Detalle del Evento'"
        >
            <div class="space-y-4">
                <!-- Badges de Tipo y Fechas -->
                <div class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-3">
                    <span
                        :class="[
                            'px-2.5 py-1 text-xs font-bold rounded-lg uppercase tracking-wider',
                            selectedEvent.type_source === 'institucional'
                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        ]"
                    >
                        {{ selectedEvent.type_source === 'institucional' ? 'Evento Institucional' : 'Privado / Personal' }}
                    </span>

                    <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <BaseIcon :path="mdiClockOutline" :size="14" />
                        <span>{{ formatEventDate(selectedEvent.start) }} - {{ formatEventDate(selectedEvent.end) }}</span>
                    </div>
                </div>

                <!-- Descripción Completa -->
                <div class="text-sm text-gray-700 dark:text-gray-200">
                    <div v-if="selectedEvent.contentFull" v-html="selectedEvent.contentFull" class="prose dark:prose-invert max-w-none text-xs sm:text-sm"></div>
                    <p v-else class="text-xs text-gray-400 italic">Sin descripción adicional para este evento.</p>
                </div>

                <!-- Acciones (Editar/Eliminar) si tiene permisos o es su evento -->
                <div v-if="canModifyEvent(selectedEvent)" class="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <button
                        type="button"
                        @click="initModal(selectedEvent.id, 'update', selectedEvent)"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 transition-colors"
                    >
                        <BaseIcon :path="mdiPencil" :size="14" />
                        <span>Modificar</span>
                    </button>

                    <button
                        type="button"
                        @click="initModal(selectedEvent.id, 'delete')"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors"
                    >
                        <BaseIcon :path="mdiTrashCan" :size="14" />
                        <span>Eliminar</span>
                    </button>
                </div>
            </div>
        </CardBoxModal>

        <!-- Modal de Crear / Editar Evento -->
        <CardBoxModal 
            v-model="isModalActive" 
            hasCancel 
            :title="rowAction === 'update' ? 'Modificar Evento' : 'Registrar Nuevo Evento'" 
            @confirm="isConfirm" 
            @cancel="isCancel"
        >
            <div class="space-y-4 text-xs sm:text-sm">
                <!-- Alerta de Error -->
                <div v-if="errorEvent" class="p-3 rounded-xl bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 flex items-center gap-2">
                    <BaseIcon :path="mdiInformation" :size="16" class="shrink-0" />
                    <span>{{ errorEvent }}</span>
                </div>

                <!-- Tipo de Evento -->
                <div>
                    <label class="block font-semibold text-gray-700 dark:text-gray-300 mb-1">Tipo de Evento</label>
                    <select 
                        v-model="formEvent.type_source" 
                        class="w-full text-xs sm:text-sm rounded-xl border-gray-200 dark:border-gray-700 disabled:opacity-75 disabled:bg-gray-100 dark:disabled:bg-gray-700"
                        :disabled="!canCreateInstitutionalEvent"
                    >
                        <option v-if="canCreateInstitutionalEvent" value="institucional">Institucional (Actividad oficial de la escuela)</option>
                        <option value="user_private">Privado (Personal / Recordatorio propio)</option>
                    </select>
                    <p v-if="!canCreateInstitutionalEvent" class="text-[11px] text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                        <BaseIcon :path="mdiLockOutline" :size="13" class="text-amber-500" />
                        <span>Su perfil registra eventos de tipo privado para su agenda personal.</span>
                    </p>
                </div>

                <!-- Título -->
                <div>
                    <label class="block font-semibold text-gray-700 dark:text-gray-300 mb-1">Título del Evento</label>
                    <input 
                        type="text" 
                        v-model="formEvent.title" 
                        placeholder="Ej. Ceremonia Cívica, Reunión de Coordinación, Entrega de Notas..."
                        class="w-full text-xs sm:text-sm rounded-xl border-gray-200 dark:border-gray-700" 
                        required
                    />
                </div>

                <!-- Fechas de Inicio y Fin -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label class="block font-semibold text-gray-700 dark:text-gray-300 mb-1">Fecha y Hora de Inicio</label>
                        <input type="datetime-local" v-model="formEvent.start" class="w-full text-xs sm:text-sm rounded-xl border-gray-200 dark:border-gray-700" required />
                    </div>
                    <div>
                        <label class="block font-semibold text-gray-700 dark:text-gray-300 mb-1">Fecha y Hora de Fin</label>
                        <input type="datetime-local" v-model="formEvent.end" class="w-full text-xs sm:text-sm rounded-xl border-gray-200 dark:border-gray-700" required />
                    </div>
                </div>

                <!-- Descripción -->
                <div>
                    <label class="block font-semibold text-gray-700 dark:text-gray-300 mb-1">Detalles / Descripción (Opcional)</label>
                    <textarea 
                        v-model="formEvent.contentFull" 
                        rows="3" 
                        placeholder="Detalles de la actividad, aula, responsable o indicaciones..."
                        class="w-full text-xs sm:text-sm rounded-xl border-gray-200 dark:border-gray-700"
                    ></textarea>
                </div>
            </div>
        </CardBoxModal>

        <!-- Modal de Confirmación de Eliminación -->
        <CardBoxModal 
            v-model="isModalDangerActive" 
            title="Confirmar Eliminación" 
            button="danger" 
            hasCancel 
            @confirm="isConfirm" 
            @cancel="isCancel"
        >
            <p class="text-sm text-gray-700 dark:text-gray-300">
                ¿Está seguro de eliminar este evento de la agenda? Esta acción no se puede deshacer.
            </p>
        </CardBoxModal>

        <!-- Contenedor Principal Multidispositivo -->
        <div class="space-y-6 pb-12">
            <!-- Pestañas de Navegación Superior: Solo visible para Estudiantes / Apoderados -->
            <div v-if="isStudent" class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-1.5 sm:p-2">
                <div class="flex items-center gap-2 overflow-x-auto scrollbar-none">
                    <button
                        type="button"
                        @click="activeMainTab = 'calendar'"
                        :class="[
                            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all duration-150',
                            activeMainTab === 'calendar'
                                ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/25'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        ]"
                    >
                        <BaseIcon :path="mdiCalendarMonth" :size="18" />
                        <span>Calendario y Eventos</span>
                        <span class="px-2 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
                            {{ formattedEvents.length }}
                        </span>
                    </button>

                    <button
                        type="button"
                        @click="switchStudentTab"
                        :class="[
                            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all duration-150',
                            activeMainTab === 'student_agenda'
                                ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/25'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        ]"
                    >
                        <BaseIcon :path="mdiSchool" :size="18" />
                        <span>Agenda Escolar del Estudiante</span>
                    </button>
                </div>
            </div>

            <!-- ================= SECCIÓN 1: CALENDARIO Y EVENTOS ================= -->
            <div v-show="activeMainTab === 'calendar'" class="space-y-4">
                <!-- Toolbar de Filtros y Controles -->
                <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                    <!-- Filtros por tipo de evento -->
                    <div class="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
                        <button
                            type="button"
                            @click="filterEventType = 'all'"
                            :class="[
                                'px-3 py-1.5 rounded-xl font-bold transition-all whitespace-nowrap',
                                filterEventType === 'all'
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                            ]"
                        >
                            Todos ({{ events?.length || 0 }})
                        </button>
                        <button
                            type="button"
                            @click="filterEventType = 'institucional'"
                            :class="[
                                'px-3 py-1.5 rounded-xl font-bold flex items-center gap-1.5 transition-all whitespace-nowrap',
                                filterEventType === 'institucional'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-100'
                            ]"
                        >
                            <span class="w-2 h-2 rounded-full bg-blue-500"></span>
                            <span>Institucionales ({{ institutionalCount }})</span>
                        </button>
                        <button
                            type="button"
                            @click="filterEventType = 'user_private'"
                            :class="[
                                'px-3 py-1.5 rounded-xl font-bold flex items-center gap-1.5 transition-all whitespace-nowrap',
                                filterEventType === 'user_private'
                                    ? 'bg-amber-500 text-white shadow-sm'
                                    : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 hover:bg-amber-100'
                            ]"
                        >
                            <span class="w-2 h-2 rounded-full bg-amber-500"></span>
                            <span>Privados ({{ privateCount }})</span>
                        </button>
                    </div>

                    <!-- Conmutador de vista de calendario y Botón Nuevo Evento -->
                    <div class="flex items-center gap-2 self-end md:self-auto">
                        <div class="inline-flex p-1 bg-gray-100 dark:bg-gray-700 rounded-xl">
                            <button
                                type="button"
                                @click="calendarView = 'month'; viewDisplayMode = 'calendar'"
                                :class="[
                                    'px-3 py-1 text-xs font-semibold rounded-lg transition-all',
                                    calendarView === 'month' && viewDisplayMode === 'calendar'
                                        ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-xs'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                ]"
                            >
                                Mes
                            </button>
                            <button
                                type="button"
                                @click="calendarView = 'week'; viewDisplayMode = 'calendar'"
                                :class="[
                                    'px-3 py-1 text-xs font-semibold rounded-lg transition-all',
                                    calendarView === 'week' && viewDisplayMode === 'calendar'
                                        ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-xs'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                ]"
                            >
                                Semana
                            </button>
                            <button
                                type="button"
                                @click="viewDisplayMode = 'list'"
                                :class="[
                                    'px-3 py-1 text-xs font-semibold rounded-lg transition-all',
                                    viewDisplayMode === 'list'
                                        ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-xs'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                ]"
                            >
                                Lista
                            </button>
                        </div>

                        <!-- <button
                            v-if="canCreate"
                            type="button"
                            @click="initModal(0, 'create')"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm shadow-indigo-500/20 active:scale-95 transition-all cursor-pointer whitespace-nowrap"
                        >
                            <BaseIcon :path="mdiPlusCircle" :size="16" />
                            <span>Crear Evento</span>
                        </button> -->
                    </div>
                </div>

                <!-- Vista Calendario VueCal -->
                <div v-show="viewDisplayMode === 'calendar'" class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-3 sm:p-5 overflow-hidden">
                    <div class="w-full min-h-[520px] lg:min-h-[680px]">
                        <VueCal
                            :transitions="false"
                            small
                            :time="false"
                            locale="es"
                            hide-view-selector
                            :disable-views="['years', 'year', 'day']"
                            :events="formattedEvents"
                            :active-view="calendarView"
                            hide-weekends
                            :on-event-click="onEventClick"
                            class="cal-modern rounded-xl overflow-hidden"
                        />
                    </div>
                </div>

                <!-- Vista Lista de Eventos (Especialmente cómoda en pantallas móviles) -->
                <div v-show="viewDisplayMode === 'list'" class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 sm:p-5 space-y-3">
                    <h4 class="text-sm font-bold text-gray-900 dark:text-white mb-3">Listado de Actividades y Eventos</h4>

                    <div v-if="formattedEvents.length" class="space-y-2.5">
                        <div
                            v-for="ev in formattedEvents"
                            :key="ev.id"
                            @click="onEventClick(ev)"
                            class="p-4 rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-indigo-200 dark:hover:border-indigo-800/60 cursor-pointer transition-all"
                        >
                            <div class="space-y-1 min-w-0">
                                <div class="flex items-center gap-2">
                                    <span
                                        :class="[
                                            'px-2 py-0.5 text-[10px] font-extrabold rounded-md uppercase',
                                            ev.type_source === 'institucional'
                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
                                        ]"
                                    >
                                        {{ ev.type_source === 'institucional' ? 'Institucional' : 'Privado' }}
                                    </span>
                                    <h5 class="text-sm font-bold text-gray-900 dark:text-white truncate">{{ ev.title }}</h5>
                                </div>
                                <p v-if="ev.contentFull" class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                    {{ ev.contentFull }}
                                </p>
                            </div>

                            <div class="text-xs text-gray-400 flex items-center gap-1.5 shrink-0">
                                <BaseIcon :path="mdiClockOutline" :size="15" class="text-indigo-500" />
                                <span>{{ formatEventDate(ev.start) }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-else class="text-center py-10 text-gray-400 text-xs">
                        No hay eventos que mostrar en este filtro.
                    </div>
                </div>
            </div>

            <!-- ================= SECCIÓN 2: AGENDA ESCOLAR DEL ESTUDIANTE ================= -->
            <div v-if="isStudent" v-show="activeMainTab === 'student_agenda'" class="space-y-6">
                <!-- Barra de Fecha y Navegación Diaria -->
                <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div class="flex items-center gap-2 w-full sm:w-auto justify-between">
                        <button
                            type="button"
                            @click="changeStudentAgendaDay(-1)"
                            class="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 transition-colors"
                            title="Día anterior"
                        >
                            <BaseIcon :path="mdiChevronLeft" :size="20" />
                        </button>

                        <div class="text-center px-3">
                            <h4 class="text-sm sm:text-base font-bold text-gray-900 dark:text-white capitalize">
                                {{ currentDayName || 'Agenda Escolar' }}
                            </h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                {{ currentFormattedDate }}
                                <span v-if="studentAgendaData?.classroom?.label || studentAgendaData?.classroom?.name" class="ml-1 font-semibold text-indigo-600">
                                    • Aula: {{ studentAgendaData.classroom.label || studentAgendaData.classroom.name }}
                                </span>
                            </p>
                        </div>

                        <button
                            type="button"
                            @click="changeStudentAgendaDay(1)"
                            class="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 transition-colors"
                            title="Día siguiente"
                        >
                            <BaseIcon :path="mdiChevronRight" :size="20" />
                        </button>
                    </div>

                    <!-- Selector Directo de Fecha -->
                    <div class="flex items-center gap-2 w-full sm:w-auto justify-end">
                        <input
                            type="date"
                            v-model="studentAgendaDate"
                            @change="loadStudentAgenda(studentAgendaDate)"
                            class="text-xs rounded-xl border-gray-200 dark:border-gray-700 py-1.5"
                        />
                        <button
                            type="button"
                            @click="loadStudentAgenda(getLocalDateString())"
                            class="px-3 py-1.5 text-xs font-bold rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 transition-colors"
                        >
                            Hoy
                        </button>
                    </div>
                </div>

                <!-- Loader de Agenda Estudiantil -->
                <div v-if="studentAgendaLoading" class="p-12 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm animate-pulse space-y-3">
                    <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto"></div>
                    <div class="h-4 bg-gray-100 dark:bg-gray-700/60 rounded w-1/2 mx-auto"></div>
                    <p class="text-xs text-gray-400 mt-2">Cargando agenda escolar del aula...</p>
                </div>

                <!-- Contenido de la Agenda Diaria -->
                <div v-else-if="studentAgendaData?.success" class="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <!-- Columna Izquierda: Clases del Día, Temas y Tareas -->
                    <div class="lg:col-span-8 space-y-4">
                        <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-5 space-y-4">
                            <div class="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/60 pb-3">
                                <div class="flex items-center gap-2">
                                    <BaseIcon :path="mdiBookOpenVariant" :size="20" class="text-indigo-600" />
                                    <h4 class="text-sm font-bold text-gray-900 dark:text-white">Materias y Actividades del Día</h4>
                                </div>
                                <span v-if="studentAgendaData?.header?.weekNumber || studentAgendaData?.semana_academica" class="text-xs font-semibold text-gray-400">
                                    Semana Académica {{ studentAgendaData?.header?.weekNumber || studentAgendaData?.semana_academica }}
                                </span>
                            </div>

                            <div v-if="agendaItemsList.length" class="space-y-3">
                                <div
                                    v-for="(item, idx) in agendaItemsList"
                                    :key="idx"
                                    class="p-4 rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 space-y-2"
                                >
                                    <div class="flex items-start justify-between gap-2">
                                        <h5 class="text-sm font-bold text-gray-900 dark:text-white">{{ item.subject || item.subject_name }}</h5>
                                        <span
                                            v-if="item.has_homework"
                                            class="px-2 py-0.5 text-[10px] font-bold rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 shrink-0"
                                        >
                                            Tarea encargada
                                        </span>
                                    </div>

                                    <p class="text-xs text-gray-600 dark:text-gray-300">
                                        <strong class="text-gray-500">Tema avanzado:</strong> {{ item.topic || 'Sin tema registrado' }}
                                    </p>

                                    <div v-if="item.has_homework && item.homework" class="p-2.5 rounded-lg bg-white dark:bg-gray-800 border border-amber-100 dark:border-amber-900/30 text-xs text-amber-900 dark:text-amber-200">
                                        <strong>Detalle de tarea:</strong> {{ item.homework }}
                                    </div>
                                </div>
                            </div>

                            <p v-else class="text-xs text-gray-400 italic text-center py-6">
                                No se registraron materias ni tareas programadas para esta fecha.
                            </p>
                        </div>
                    </div>

                    <!-- Columna Derecha: Asistencia y Horario del Aula -->
                    <div class="lg:col-span-4 space-y-5">
                        <!-- Card de Asistencia -->
                        <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 text-center space-y-2">
                            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Asistencia de la Jornada</p>
                            <span
                                :class="[
                                    'inline-flex items-center px-4 py-1.5 rounded-xl text-sm font-black',
                                    attendanceTextDisplay === 'ASISTIÓ'
                                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                        : attendanceTextDisplay === 'FALTA'
                                        ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300'
                                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                ]"
                            >
                                {{ attendanceTextDisplay }}
                            </span>
                        </div>

                        <!-- Card de Horario Semanal Resumido -->
                        <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 space-y-3">
                            <div class="flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/60 pb-2">
                                <BaseIcon :path="mdiClockOutline" :size="18" class="text-indigo-600" />
                                <h5 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Horario del Día</h5>
                            </div>

                            <div v-if="todaySchedulesList.length" class="space-y-2 text-xs">
                                <div
                                    v-for="s in todaySchedulesList"
                                    :key="s.id"
                                    class="p-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between"
                                >
                                    <span class="font-bold text-gray-800 dark:text-gray-200">
                                        {{ s.course?.label || s.course?.name || s.label_break || 'Clase' }}
                                    </span>
                                    <span class="text-gray-400 font-mono text-[11px]">
                                        {{ s.start_time?.slice(0, 5) }} - {{ s.end_time?.slice(0, 5) }}
                                    </span>
                                </div>
                            </div>
                            <p v-else class="text-xs text-gray-400 italic text-center py-2">
                                Sin bloques de horario para hoy.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Mensaje para usuarios sin matrícula de estudiante activa -->
                <div v-else class="p-10 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm space-y-2">
                    <BaseIcon :path="mdiSchool" :size="32" class="text-indigo-500 mx-auto" />
                    <h4 class="text-sm font-bold text-gray-800 dark:text-white">Agenda Estudiantil Digital</h4>
                    <p class="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {{ studentAgendaData?.message || 'Inicie sesión como estudiante o apoderado para visualizar el seguimiento diario de clases y tareas.' }}
                    </p>
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>

<style>
/* Estilos modernos y elegantes para VueCal en Agenda */
.vuecal {
    border-radius: 1rem;
    border: none;
    font-family: inherit;
}

.vuecal__title-bar {
    background-color: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
    padding: 0.5rem;
    border-radius: 1rem 1rem 0 0;
}

.vuecal__menu {
    background-color: #4f46e5 !important;
    border-radius: 0.75rem;
}

.vuecal__view-btn {
    font-size: 0.75rem;
    font-weight: 700;
}

/* Eventos Institucionales: Gradiente azul/índigo con barra lateral destacada */
.vuecal__event.institucional {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
    color: #ffffff !important;
    border-radius: 8px !important;
    border-left: 4px solid #1d4ed8 !important;
    font-weight: 700 !important;
    font-size: 0.75rem !important;
    padding: 3px 6px !important;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.2) !important;
}

/* Eventos Privados: Gradiente ámbar/dorado con barra lateral destacada */
.vuecal__event.user_private {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
    color: #ffffff !important;
    border-radius: 8px !important;
    border-left: 4px solid #b45309 !important;
    font-weight: 700 !important;
    font-size: 0.75rem !important;
    padding: 3px 6px !important;
    box-shadow: 0 1px 3px rgba(217, 119, 6, 0.2) !important;
}

.vuecal__cell--today {
    background-color: #eef2ff !important;
}

.vuecal__cell-events-count {
    background-color: #4f46e5 !important;
    color: #ffffff !important;
}
</style>
